import { formatDate, formatDateTime } from "@/lib/format";
import { useState } from "react";
import { useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getInvoice,
  listInvoiceLineItems,
  getInvoiceTransactions,
  issueInvoice,
  cancelInvoice,
  sendInvoiceReminder,
  getInvoicePdfUrl,
  listPayments,
  getPayment,
  reversePayment,
  type InvoiceDto,
  type InvoiceLineItemDto,
  type InvoiceTransactionMappingDto,
  type PaymentRecordSummaryDto,
} from "@/lib/invoices-payments";
import { EmailThreadTab } from "@/components/email/EmailThreadTab";
import { apiFetchBlob } from "@/lib/api";
import {
  InvoiceStatusBadge,
  InvoicePdfFrame,
  InvoiceTotalsTable,
  type InvoiceStatusCode,
} from "@/components/invoices-payments";
import { PaymentMethodBadge } from "@/components/payment-method-badge";
import { useAuth } from "@/app/auth-context";
import { DetailPage } from "@/archetypes/DetailPage";
import { PageHeader } from "@/components/page-header";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { RecordPaymentModal } from "@/pages/payments/RecordPaymentModal";
import { EditPaymentModal } from "@/pages/payments/EditPaymentModal";
import { PrintInvoiceLabelsDialog } from "@/components/invoices/print-invoice-labels-dialog";
import { Send, Download, Plus, Bell, FileText, Copy, Ban, MoreHorizontal, Eye, Tags } from "lucide-react";

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------


function OverviewFieldInline({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      <span className="text-sm font-semibold text-[#0f3453]">{value}</span>
    </div>
  );
}

function OverviewTab({ invoice }: { invoice: InvoiceDto }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5">
        <div className="mb-2 border-b border-slate-100 pb-2">
          <h3 className="text-base font-bold text-[#0f3453]">
            Financial Details
          </h3>
        </div>
        <div className="flex flex-col">
          <OverviewFieldInline label="Total (inc GST)" value={fmt.format(invoice.invoiceTotalIncGst)} />
          <OverviewFieldInline label="Balance outstanding" value={fmt.format(invoice.balanceOutstanding)} />
          <OverviewFieldInline label="Payment terms" value="—" />
          <OverviewFieldInline label="Paid on" value={invoice.paidOn ? formatDateTime(invoice.paidOn) : "—"} />
        </div>
      </section>

      <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5">
        <div className="mb-2 border-b border-slate-100 pb-2">
          <h3 className="text-base font-bold text-[#0f3453]">
            Client & Reference
          </h3>
        </div>
        <div className="flex flex-col">
          <OverviewFieldInline label="Client" value={invoice.clientId != null ? `#${invoice.clientId}` : "—"} />
          <OverviewFieldInline label="Account" value={invoice.accountId != null ? `#${invoice.accountId}` : "—"} />
          <OverviewFieldInline label="Linked job" value={invoice.jobId != null ? `#${invoice.jobId}` : "—"} />
          <OverviewFieldInline label="Originating quote" value="—" />
        </div>
      </section>

      <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5 md:col-span-2">
        <div className="mb-4 border-b border-slate-100 pb-2">
          <h3 className="text-base font-bold text-[#0f3453]">
            Notes
          </h3>
        </div>
        <div className="text-sm whitespace-pre-wrap">
          {invoice.commentToShowOnInvoice ?? "—"}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line items tab
// ---------------------------------------------------------------------------

function LineItemsTab({ invoice }: { invoice: InvoiceDto }) {
  const { data, isLoading } = useQuery({
    queryKey: ["invoice-line-items", invoice.invoiceId],
    queryFn: () => listInvoiceLineItems(invoice.invoiceId),
  });

  // Description column carries no explicit width so it absorbs the remaining
  // space; the number/flag columns are pinned narrow for readability (#12).
  const cols: DataGridColumn<InvoiceLineItemDto>[] = [
    { id: "desc", header: "Description", accessor: (r) => r.description },
    { id: "qty",  header: "Qty",         accessor: (r) => r.quantity, align: "right", width: "5rem" },
    { id: "unit", header: "Unit (ex GST)", accessor: (r) => fmt.format(r.unitPriceExGst), align: "right", width: "8rem" },
    { id: "disc", header: "Disc %",      accessor: (r) => `${r.discountPct}`, align: "right", width: "6rem" },
    { id: "total", header: "Total (ex GST)", accessor: (r) => fmt.format(r.totalExGst), align: "right", width: "9rem" },
    { id: "gst",  header: "GST?",        accessor: (r) => (r.gstApplicable ? "✓" : "—"), align: "center", width: "5rem" },
    { id: "credit", header: "Credit?",   accessor: (r) => (r.isCredit ? "✓" : "—"), align: "center", width: "5rem" },
  ];

  return (
    <div className="space-y-4">
      <DataGrid
        rows={data ?? []}
        columns={cols}
        rowKey={(r) => r.lineItemId}
        totalRows={(data ?? []).length}
        pageIndex={0}
        pageSize={1000}
        loading={isLoading}
        emptyHeadline="No line items."
        onPageChange={() => {}}
      />
      <div className="flex justify-end">
        <InvoiceTotalsTable
          subtotalExGst={invoice.invoiceTotalExGst}
          gst={invoice.gst}
          totalIncGst={invoice.invoiceTotalIncGst}
          balanceOutstanding={invoice.balanceOutstanding}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transactions tab
// ---------------------------------------------------------------------------

function TransactionsTab({ invoiceId }: { invoiceId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["invoice-transactions", invoiceId],
    queryFn: () => getInvoiceTransactions(invoiceId),
  });

  const cols: DataGridColumn<InvoiceTransactionMappingDto>[] = [
    { id: "date", header: "Date", accessor: (r) => r.createdOn ? formatDateTime(r.createdOn) : "—" },
    { id: "tran", header: "Tran ID", accessor: (r) => r.tranId ?? "—" },
    { id: "amount", header: "Amount", accessor: (r) => fmt.format(r.amount), align: "right" },
    { id: "note", header: "Note", accessor: (r) => r.note ?? "—" },
  ];

  return (
    <DataGrid
      rows={data ?? []}
      columns={cols}
      rowKey={(r) => r.invoiceTransactionMapId}
      totalRows={(data ?? []).length}
      pageIndex={0}
      pageSize={1000}
      loading={isLoading}
      emptyHeadline="No transactions."
      onPageChange={() => {}}
    />
  );
}

// ---------------------------------------------------------------------------
// Payments tab
// ---------------------------------------------------------------------------

function ReversePaymentDialog({
  open,
  onOpenChange,
  payment,
  onReversed,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  payment: PaymentRecordSummaryDto;
  onReversed: () => void;
}) {
  const [reason, setReason] = useState("");

  // Fetch full payment to get the rowVersion needed for optimistic concurrency.
  const { data: full, isLoading: loadingFull } = useQuery({
    queryKey: ["payment", payment.paymentRecordId],
    queryFn: () => getPayment(payment.paymentRecordId),
    enabled: open,
  });

  const reverseMut = useMutation({
    mutationFn: () =>
      reversePayment(payment.paymentRecordId, {
        reason,
        rowVersion: full?.rowVersion ?? null,
      }),
    onSuccess: () => {
      toast.success("Payment reversed");
      onOpenChange(false);
      setReason("");
      onReversed();
    },
    onError: () => toast.error("Failed to reverse payment"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reverse payment</DialogTitle>
          <DialogDescription>
            {`Reversing ${fmt.format(payment.tranAmount)} payment. This action cannot be undone.`}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim()) reverseMut.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="reverse-reason">Reason *</Label>
            <textarea
              id="reverse-reason"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Back
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={reverseMut.isPending || loadingFull || !reason.trim()}
            >
              {reverseMut.isPending ? "Reversing…" : "Reverse payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentsTab({
  invoiceId,
  onRecordPayment,
}: {
  invoiceId: number;
  onRecordPayment: () => void;
}) {
  const qc = useQueryClient();
  const { permissions } = useAuth();
  const canWrite = permissions.has("payments:write");

  const [reversingPayment, setReversingPayment] = useState<PaymentRecordSummaryDto | null>(null);
  const [editingPayment, setEditingPayment] = useState<PaymentRecordSummaryDto | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["invoice-payments", invoiceId],
    queryFn: () => listPayments({ invoiceId }),
  });

  const rows = data?.items ?? [];

  const cols: DataGridColumn<PaymentRecordSummaryDto>[] = [
    {
      id: "method",
      header: "Method",
      accessor: (r) => (
        <PaymentMethodBadge code={r.paymentTypeCode} />
      ),
    },
    {
      id: "amount",
      header: "Amount",
      accessor: (r) => fmt.format(r.tranAmount),
      align: "right",
    },
    {
      id: "date",
      header: "Date",
      accessor: (r) => new Date(r.transactionDate).toLocaleDateString("en-AU"),
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => r.status,
    },
    {
      id: "actions",
      header: "",
      accessor: (r) =>
        canWrite && r.status === "Posted" ? (
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingPayment(r)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setReversingPayment(r)}
            >
              Reverse
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-3">
      <DataGrid
        rows={rows}
        columns={cols}
        rowKey={(r) => r.paymentRecordId}
        totalRows={rows.length}
        pageIndex={0}
        pageSize={1000}
        loading={isLoading}
        emptyHeadline="No payments recorded."
        onPageChange={() => {}}
      />
      <div className="flex justify-end pt-2">
        <Button onClick={onRecordPayment}>
          <Plus className="mr-2 h-4 w-4" />
          Record Payment
        </Button>
      </div>
      {reversingPayment && (
        <ReversePaymentDialog
          open={reversingPayment !== null}
          onOpenChange={(o) => { if (!o) setReversingPayment(null); }}
          payment={reversingPayment}
          onReversed={() => {
            void qc.invalidateQueries({ queryKey: ["invoice-payments", invoiceId] });
            void qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
          }}
        />
      )}
      {editingPayment && (
        <EditPaymentModal
          open={editingPayment !== null}
          onOpenChange={(o) => { if (!o) setEditingPayment(null); }}
          paymentRecordId={editingPayment.paymentRecordId}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: ["invoice-payments", invoiceId] });
            void qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cancel modal
// ---------------------------------------------------------------------------

function CancelInvoiceDialog({
  open,
  onOpenChange,
  invoice,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  invoice: InvoiceDto;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");

  const cancelMut = useMutation({
    mutationFn: () => cancelInvoice(invoice.invoiceId, { reason, rowVersion: invoice.rowVersion }),
    onSuccess: () => {
      toast.success("Invoice cancelled");
      void qc.invalidateQueries({ queryKey: ["invoice", invoice.invoiceId] });
      onOpenChange(false);
      setReason("");
    },
    onError: () => toast.error("Failed to cancel invoice"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel invoice</DialogTitle>
          <DialogDescription>
            Provide a reason. This action will mark the invoice as Cancelled.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); if (reason.trim()) cancelMut.mutate(); }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="cancel-reason">Reason *</Label>
            <Input
              id="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Back
            </Button>
            <Button type="submit" variant="destructive" disabled={cancelMut.isPending || !reason.trim()}>
              {cancelMut.isPending ? "Cancelling…" : "Cancel invoice"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Issue confirmation dialog
// ---------------------------------------------------------------------------

function IssueInvoiceDialog({
  open,
  onOpenChange,
  invoice,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  invoice: InvoiceDto;
}) {
  const qc = useQueryClient();
  const issueMut = useMutation({
    mutationFn: () => issueInvoice(invoice.invoiceId, { rowVersion: invoice.rowVersion }),
    onSuccess: () => {
      toast.success("Invoice issued");
      void qc.invalidateQueries({ queryKey: ["invoice", invoice.invoiceId] });
      onOpenChange(false);
    },
    onError: () => toast.error("Failed to issue invoice"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Issue invoice</DialogTitle>
          <DialogDescription>
            This finalises the invoice ({invoice.invoiceReferenceNo}) and marks it as Sent.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Back</Button>
          <Button onClick={() => issueMut.mutate()} disabled={issueMut.isPending}>
            {issueMut.isPending ? "Issuing…" : "Issue invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Primary action bar
// ---------------------------------------------------------------------------

function InvoiceActions({
  invoice,
  showPreview,
  setShowPreview,
}: {
  invoice: InvoiceDto;
  showPreview: boolean;
  setShowPreview: (v: boolean) => void;
}) {
  const status = invoice.invoiceStatusCode as InvoiceStatusCode;
  const { permissions } = useAuth();
  // The same claim the route itself enforces (`POST /api/invoices/{id}/delivery-sticker/print`):
  // labels are a dispatch artefact whichever screen launched them. Finance does not hold it, so
  // ungated this offered the one role that lives on this page an action that always 403s.
  const canPrintLabels = permissions.has("jobs:status-change");
  const [issueOpen, setIssueOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);

  const reminderMut = useMutation({
    mutationFn: () => sendInvoiceReminder(invoice.invoiceId, { customMessage: null }),
    onSuccess: () => toast.success("Reminder sent"),
    onError: () => toast.error("Failed to send reminder"),
  });

  const canCancel = ["Sent", "PartPaid", "Paid", "OverPaid", "Overdue"].includes(status);

  return (
    <TooltipProvider>
      <div className="flex flex-nowrap items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`rounded-full h-8 w-8 border-transparent ${showPreview ? "bg-primary text-white hover:bg-primary/90" : "bg-info/15 text-primary hover:bg-info/25"}`}
              onClick={() => setShowPreview(!showPreview)}
            >
              <Eye className="h-4 w-4" />
              <span className="sr-only">{showPreview ? "Hide Preview" : "Show Preview"}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{showPreview ? "Hide Preview" : "Show Preview"}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-8 w-8 border-transparent bg-info/15 text-primary hover:bg-info/25"
              onClick={async () => {
                try {
                  const blob = await apiFetchBlob(getInvoicePdfUrl(invoice.invoiceId));
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `invoice-${invoice.invoiceReferenceNo}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error("Failed to download PDF");
                }
              }}
            >
              <Download className="h-4 w-4" />
              <span className="sr-only">Download PDF</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Download PDF</TooltipContent>
        </Tooltip>

        {status === "Draft" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full h-8 w-8 border-transparent bg-info/15 text-primary hover:bg-info/25"
                onClick={() => setIssueOpen(true)}
              >
                <Send className="h-4 w-4" />
                <span className="sr-only">Issue Invoice</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Issue Invoice</TooltipContent>
          </Tooltip>
        )}

        {(status === "Sent" || status === "PartPaid" || status === "Overdue") && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full h-8 w-8 border-transparent bg-info/15 text-primary hover:bg-info/25"
                onClick={() => reminderMut.mutate()}
                disabled={reminderMut.isPending}
              >
                <Bell className="h-4 w-4" />
                <span className="sr-only">Send Reminder</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Send Reminder</TooltipContent>
          </Tooltip>
        )}

        {/* No status guard on the menu itself any more: parcel labels are printable from Draft
            onwards — the parcels are labelled when they leave, which is not tied to where the
            invoice has got to, and plenty of deliveries ship before anyone issues one — so the
            menu now always has at least one item, live or disabled. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="outline" className="rounded-full h-8 w-8 border-transparent bg-info/15 text-primary hover:bg-info/25">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">More actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* Every status but Cancelled: a cancelled invoice is a voided sale, so its parcels
                are not leaving and a label printed against it is either a mistake or a workaround.
                Draft stays in — the job-less delivery that ships before it is invoiced is the main
                reason this entry point exists at all. */}
            {status !== "Cancelled" && canPrintLabels && (
              <DropdownMenuItem onSelect={() => setLabelsOpen(true)}>
                <Tags className="mr-2 h-4 w-4" />
                Print Parcel Labels
              </DropdownMenuItem>
            )}
            {(status === "Paid" || status === "OverPaid") && (
              <DropdownMenuItem disabled>
                <FileText className="mr-2 h-4 w-4" />
                View Receipt (Soon)
              </DropdownMenuItem>
            )}
            {status === "Cancelled" && (
              <DropdownMenuItem disabled>
                <Copy className="mr-2 h-4 w-4" />
                Duplicate to draft (Soon)
              </DropdownMenuItem>
            )}
            {canCancel && (
              <DropdownMenuItem className="text-destructive focus:bg-destructive/10 focus:text-destructive" onSelect={() => setCancelOpen(true)}>
                <Ban className="mr-2 h-4 w-4" />
                Cancel invoice
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <IssueInvoiceDialog open={issueOpen} onOpenChange={setIssueOpen} invoice={invoice} />
        <CancelInvoiceDialog open={cancelOpen} onOpenChange={setCancelOpen} invoice={invoice} />
        <PrintInvoiceLabelsDialog
          invoiceId={invoice.invoiceId}
          open={labelsOpen}
          onOpenChange={setLabelsOpen}
        />
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// InvoiceDetailPage
// ---------------------------------------------------------------------------

export function InvoiceDetailPage() {
  const { id = "0" } = useParams();
  const invoiceId = Number(id);
  const qc = useQueryClient();

  const [showPreview, setShowPreview] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);

  const { data: invoice } = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => getInvoice(invoiceId),
  });

  const status = invoice?.invoiceStatusCode as InvoiceStatusCode | undefined;

  const tabs = invoice
    ? [
        { id: "overview",     label: "Overview",     content: <OverviewTab invoice={invoice} /> },
        { id: "lineItems",    label: "Line items",   content: <LineItemsTab invoice={invoice} /> },
        {
          id: "payments",
          label: "Payments",
          content: (
            <PaymentsTab
              invoiceId={invoiceId}
              onRecordPayment={() => setRecordPaymentOpen(true)}
            />
          ),
        },
        {
          id: "transactions",
          label: "Transactions",
          content: <TransactionsTab invoiceId={invoiceId} />,
        },
        {
          id: "emails",
          label: "Emails",
          content: <EmailThreadTab entityType="Invoice" entityId={invoiceId} />,
        },
        {
          id: "files",
          label: "Files",
          content: (
            <p className="text-center text-sm text-muted-foreground py-8">
              PDF blob preview is in the right pane.
            </p>
          ),
        },
        {
          id: "audit",
          label: "Audit",
          content: (
            <p className="text-center text-sm text-muted-foreground py-8">
              Audit history available in admin views.
            </p>
          ),
        },
      ]
    : [];

  return (
    <>
      <DetailPage
        title={invoice ? `Invoice ${invoice.invoiceReferenceNo}` : "Loading…"}
        refPill={invoice && status ? <InvoiceStatusBadge code={status} /> : undefined}
        headerVariant="title"
        headerOverride={
          invoice ? (
            <div className="mb-4 bg-white rounded-lg border border-slate-200 px-4 py-2.5 shadow-sm">
              <PageHeader 
                title={`Invoice ${invoice.invoiceReferenceNo}`} 
                refPill={status ? <InvoiceStatusBadge code={status} /> : undefined} 
                className="hidden" 
              />
              <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-2 text-[11px]">
                <div><span className="text-slate-500 font-medium uppercase tracking-wider mr-2">Issue Date</span><span className="font-semibold text-slate-800">{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : "—"}</span></div>
                <div><span className="text-slate-500 font-medium uppercase tracking-wider mr-2">Due Date</span><span className="font-semibold text-slate-800">{invoice.dueDate ? formatDate(invoice.dueDate) : "—"}</span></div>
                <div><span className="text-slate-500 font-medium uppercase tracking-wider mr-2">Issued At</span><span className="font-semibold text-slate-800">{invoice.issuedAtUtc ? formatDateTime(invoice.issuedAtUtc) : "—"}</span></div>
                <div><span className="text-slate-500 font-medium uppercase tracking-wider mr-2">Reminder Issued</span><span className="font-semibold text-slate-800">{invoice.reminderIssuedOn ? formatDateTime(invoice.reminderIssuedOn) : "—"}</span></div>
                <div><span className="text-slate-500 font-medium uppercase tracking-wider mr-2">MYOB Exported</span><span className="font-semibold text-slate-800">{invoice.myobExportedAtUtc ? formatDateTime(invoice.myobExportedAtUtc) : "—"}</span></div>
              </div>
            </div>
          ) : (
            <PageHeader title="Loading…" className="hidden" />
          )
        }
        tabActions={invoice ? <InvoiceActions invoice={invoice} showPreview={showPreview} setShowPreview={setShowPreview} /> : undefined}
        tabs={tabs}
        defaultTab="overview"
        rightPane={invoice && showPreview ? <InvoicePdfFrame invoiceId={invoice.invoiceId} /> : undefined}
        backTo="/invoices"
      />

      {invoice && (
        <RecordPaymentModal
          open={recordPaymentOpen}
          onOpenChange={setRecordPaymentOpen}
          invoiceId={invoiceId}
          accountId={invoice.accountId ?? undefined}
          defaultAmount={invoice.balanceOutstanding}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: ["invoice-payments", invoiceId] });
            void qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
            void qc.invalidateQueries({ queryKey: ["payments"] });
          }}
        />
      )}
    </>
  );
}
